import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AlgoMode, Currency, Strategy } from "@front/m19-api-client";
import { injectStrategyService } from "@front/m19-injection-token";
import { StrategyEx } from "@front/m19-models";
import { AccountSelectionService, TacosStrategiesService } from "@front/m19-services";
import { IButtonDirective, MODAL_DATA, ModalOptions, ModalRef, ToasterService } from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
  AlgoModeStrKeys,
  toTargetConfig,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { DateTime } from "luxon";
import { switchMap } from "rxjs";

@Component({
  selector: "app-switch-target-algo-modal",
  templateUrl: "./switch-target-algo-modal.component.html",
  providers: [DatePipe],
  imports: [AlgoModeSelectionComponent, TranslocoDirective, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwitchTargetAlgoModalComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "sm" };

  private toasterService = inject(ToasterService);
  private translocoService = inject(TranslocoService);
  private tacosStrategyService = inject(TacosStrategiesService);
  private accountSelectionService = inject(AccountSelectionService);

  readonly data = inject<TargetAlgoModeSelectionData>(MODAL_DATA);
  private readonly strategyService = injectStrategyService(this.data.strategy.campaignType);

  readonly ref = inject(ModalRef);

  protected readonly AlgoMode = AlgoMode;
  protected readonly Math = Math;

  readonly algoModeConfig = signal<Partial<AlgoModeConfig> | undefined>(undefined);
  readonly algoModeConfigInit = signal<Partial<AlgoModeConfig> | undefined>(undefined);
  readonly disabled = computed(() => !this.algoModeConfig() || !this.algoModeConfig()?.isValid);
  private readonly tacosList = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.tacosStrategyService.getTacosStrategyList(am!.accountId, am!.marketplace!)),
    ),
  );

  readonly updateLoading = signal(false);

  nextMonth?: Date;
  endOfCurrentMonth?: Date;

  ngOnInit(): void {
    this.algoModeConfig.set({
      ...this.data.strategy,
      acosTarget: this.data.strategy.acosTarget ? Math.round(this.data.strategy.acosTarget * 100) : undefined,
      tacosTarget:
        this.data.strategy.isSpStrategy() && this.data.strategy.tacosTarget
          ? Math.round(this.data.strategy.tacosTarget * 100)
          : undefined,
    });
    this.algoModeConfigInit.set(this.algoModeConfig());

    const today = DateTime.fromISO(this.data.strategy.today!);
    this.endOfCurrentMonth = today.endOf("month").toJSDate();
    this.nextMonth = today.startOf("month").plus({ month: 1 }).toJSDate();
  }

  update(): void {
    if (this.disabled()) return;
    this.updateLoading.set(true);
    if (this.algoModeConfig()!.algoMode === AlgoMode.TACOS_TARGET) {
      this.updateTacosTarget(this.algoModeConfig()!.tacosTarget!);
      return;
    }
    const targetName = this.translocoService.translate(AlgoModeStrKeys[this.algoModeConfig()!.algoMode!].title);
    this.strategyService
      .updateTarget(
        this.data.strategy.accountId,
        this.data.strategy.marketplace,
        this.data.strategy.strategyId!,
        this.data.organizationId,
        toTargetConfig(this.algoModeConfig()!, DateUtils.formatDateMonthForApi(this.endOfCurrentMonth!)),
      )
      .subscribe({
        next: (strategy: Strategy) => {
          this.toasterService.success(`${targetName} updated for strategy ${strategy.name}`);
          this.updateLoading.set(false);
          this.ref.close();
        },
        error: (e) => {
          this.toasterService.error(`Error updating ${targetName}: ${e}`);
          this.updateLoading.set(false);
          this.ref.close();
        },
      });
  }

  updateTacosTarget(tacosTarget: number) {
    const tacos = this.tacosList()!.find((t) => t.spStrategyId === this.data.strategy.strategyId);
    if (!tacos) {
      this.toasterService.error(`TACOS strategy not found`);
      return;
    }
    this.tacosStrategyService
      .updateTacosStrategyGroup({
        accountId: this.data.strategy.accountId,
        marketplace: this.data.strategy.marketplace,
        tacosTarget: tacosTarget / 100,
        tacosStrategyGroupId: tacos!.tacosStrategyGroupId!,
      })
      .subscribe({
        next: (_) => {
          this.toasterService.success(`TACOS target updated`);
          this.ref.close();
          this.updateLoading.set(false);
        },
        error: (e) => {
          this.toasterService.error(`Error updating TACOS target: ${e}`);
          this.ref.close();
          this.updateLoading.set(false);
        },
      });
  }
}

export interface TargetAlgoModeSelectionData {
  strategy: StrategyEx;
  organizationId: number;
  currencyCode: Currency;
  readonly?: boolean;
}
